import type { Attachment } from "@engram/core";
import { ArrowDownUp, Check, ChevronLeft, Download, File, FileAudio, FileImage, FileText, MoreHorizontal, Paperclip, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { createNote } from "../db/actions";
import { useQuery } from "../db/client";
import { attachmentUrl, useAttachmentUrl } from "./attachments";
import { queueFilesForNote } from "./editor/Editor";
import { collator } from "./format";
import { useLayout } from "./layout";
import { Menu, Popover, useAnchor } from "./Menu";

/*
 * Evernote's Files page: every attachment in every note, with Files / Media / Docs tabs, "Search file…", sort,
 * and a preview of the selected file with Go to note and Download.
 */

type Row = Attachment & { note_title: string; notebook_id: string };
type Tab = "files" | "media" | "docs";
type Sort = "recent" | "title" | "size" | "kind";

const isMedia = (mime: string) => /^(image|video|audio)\//.test(mime);

export function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0).replace(/\.0$/, "")} MB`;
}

function displayName(a: Row): string {
  // Evernote shows unnamed pictures as "image".
  if (a.filename && a.filename !== "file") return a.filename.replace(/\.[a-z0-9]{2,5}$/i, "");
  return a.mime.split("/")[0] === "image" ? "image" : "file";
}

function FileIcon({ mime, size = 16 }: { mime: string; size?: number }) {
  const Icon = mime.startsWith("image/") ? FileImage : mime.startsWith("audio/") ? FileAudio : mime === "application/pdf" ? FileText : File;
  return <Icon size={size} />;
}

export function FilesPage() {
  const [, navigate] = useLocation();
  const params = new URLSearchParams(useSearch());
  const selected = params.get("f");
  const phone = useLayout() === "narrow";
  const [tab, setTab] = useState<Tab>("files");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: Sort; desc: boolean }>({ key: "recent", desc: true });
  const [showSize, setShowSize] = useState(true);
  const [showNote, setShowNote] = useState(true);
  const sortMenu = useAnchor();
  const fileInput = useRef<HTMLInputElement>(null);

  const rows = useQuery<Row>(
    `SELECT a.*, n.title AS note_title, n.notebook_id FROM attachments a JOIN notes n ON n.id = a.note_id
     WHERE a.deleted = 0 AND n.deleted = 0 AND n.trashed_at IS NULL ORDER BY a.created DESC`,
    [],
    ["attachments", "notes"],
  );
  const total = useMemo(() => (rows ?? []).reduce((s, r) => s + r.size, 0), [rows]);
  const term = q.trim().toLowerCase();
  const list = useMemo(() => {
    const out = (rows ?? []).filter(
      (r) => (tab === "files" || (tab === "media" ? isMedia(r.mime) : !isMedia(r.mime))) && (!term || r.filename.toLowerCase().includes(term) || r.note_title.toLowerCase().includes(term)),
    );
    const dir = sort.desc ? -1 : 1;
    const cmp: Record<Sort, (a: Row, b: Row) => number> = {
      recent: (a, b) => (a.created - b.created) * dir,
      title: (a, b) => collator.compare(displayName(a), displayName(b)) * dir,
      size: (a, b) => (a.size - b.size) * dir,
      kind: (a, b) => a.mime.localeCompare(b.mime) * dir || b.created - a.created,
    };
    return out.sort(cmp[sort.key]);
  }, [rows, tab, term, sort]);

  const select = (id: string | null) => navigate(id ? `/files?f=${id}` : "/files", { replace: !phone });
  // Like the note list, open the first file.
  useEffect(() => {
    if (!phone && !selected && list[0]) select(list[0].id);
  }, [list, selected, phone]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = list.find((r) => r.id === selected) ?? rows?.find((r) => r.id === selected);

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    const title = files.length === 1 ? files[0]!.name.replace(/\.[^.]+$/, "") : `${files.length} files`;
    const id = await createNote({ title });
    queueFilesForNote(id, files);
    navigate(`/notes?n=${id}`);
  };

  const showList = !phone || !current;
  return (
    <div className={`files-page${phone ? " single" : ""}`}>
      {showList && (
        <section className="files-list">
          <header className="list-header">
            <div className="list-title-row">
              <h1 className="list-title">
                <span className="list-title-text">Files</span>
                {rows && <span className="title-count">{rows.length}</span>}
              </h1>
              <span className="files-total"><span className="files-total-label">Total size: </span>{formatSize(total)}</span>
            </div>
            <div className="files-tabs">
              <div className="seg">
                {(
                  [
                    ["files", "Files"],
                    ["media", "Media"],
                    ["docs", "Docs"],
                  ] as [Tab, string][]
                ).map(([k, label]) => (
                  <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="grow" />
              <button className="icon-btn" title="Add files (creates a note)" onClick={() => fileInput.current?.click()}>
                <Paperclip size={16} />
              </button>
              <button className="icon-btn" title="Sort options" onClick={sortMenu.open}>
                <ArrowDownUp size={16} />
              </button>
            </div>
            <label className="find-input files-find">
              <input placeholder="Search file…" value={q} onChange={(e) => setQ(e.target.value)} />
              {q ? <button className="icon-btn" aria-label="Clear" onClick={() => setQ("")}><X size={13} /></button> : <Search size={14} />}
            </label>
          </header>
          <div className="list-scroll" role="listbox" aria-label="Files">
            {list.map((r) => (
              <button key={r.id} role="option" aria-selected={r.id === selected} className={`file-row${r.id === selected ? " selected" : ""}`} onClick={() => select(r.id)}>
                <span className="file-row-icon"><FileIcon mime={r.mime} /></span>
                <span className="file-row-main">
                  <span className="file-row-name">
                    <span className="file-row-title">{displayName(r)}</span>
                    {showSize && <span className="file-row-size">({formatSize(r.size)})</span>}
                  </span>
                  {showNote && <span className="file-row-note">{r.note_title || "Untitled"}</span>}
                </span>
              </button>
            ))}
            {rows && list.length === 0 && <div className="list-empty">{term ? "No files match" : "No files yet"}</div>}
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = "";
              void addFiles(files);
            }}
          />
        </section>
      )}
      {(!phone || current) && <FilePreview file={current} onBack={phone ? () => history.back() : undefined} />}

      {sortMenu.anchor && (
        <Popover anchor={sortMenu.anchor} onClose={sortMenu.close} align="end" className="menu">
          <div className="menu-heading">Sort by</div>
          {(
            [
              ["recent", "Recent"],
              ["title", "Title"],
              ["size", "File size"],
              ["kind", "File kind"],
            ] as [Sort, string][]
          ).map(([k, label]) => (
            <button key={k} className="menu-item" onClick={() => setSort((s) => (s.key === k ? { key: k, desc: !s.desc } : { key: k, desc: k === "recent" || k === "size" }))}>
              <span className="menu-icon">{sort.key === k && <Check size={14} />}</span>
              <span className="menu-label">{label}</span>
              {sort.key === k && <span className="menu-hint">{sort.desc ? "↓" : "↑"}</span>}
            </button>
          ))}
          <div className="menu-sep" />
          <button className="menu-item" onClick={() => setShowSize((v) => !v)}>
            <span className="menu-icon">{showSize && <Check size={14} />}</span>
            <span className="menu-label">Show file size</span>
          </button>
          <button className="menu-item" onClick={() => setShowNote((v) => !v)}>
            <span className="menu-icon">{showNote && <Check size={14} />}</span>
            <span className="menu-label">Show file position</span>
          </button>
        </Popover>
      )}
    </div>
  );
}

const INLINE_PDF = typeof navigator !== "undefined" && navigator.pdfViewerEnabled === true;

function FilePreview({ file, onBack }: { file: Row | undefined; onBack?: () => void }) {
  const url = useAttachmentUrl(file?.id);
  const [, navigate] = useLocation();
  const more = useAnchor();
  if (!file) return <div className="file-preview empty" />;
  const name = file.filename || displayName(file);
  const download = async () => {
    const u = url ?? (await attachmentUrl(file.id));
    if (!u) return;
    const a = document.createElement("a");
    a.href = u;
    a.download = name;
    a.click();
  };
  const goToNote = () => navigate(`/note/${file.note_id}`);
  return (
    <section className="file-preview">
      <header className="editor-top">
        {onBack ? (
          <button className="icon-btn back" aria-label="Back" onClick={onBack}><ChevronLeft size={22} /></button>
        ) : (
          <button className="icon-btn" title="Download file" aria-label="Download file" onClick={() => void download()}><Download size={16} /></button>
        )}
        <div className="breadcrumb">
          <button className="crumb" onClick={goToNote}><span>{file.note_title || "Untitled"}</span></button>
          <span className="crumb-sep">›</span>
          <span className="crumb-note">{displayName(file)}</span>
        </div>
        <div className="editor-top-actions">
          <button className="btn primary small" onClick={goToNote}>Go to note</button>
          <button className="icon-btn" title="More actions" aria-label="More actions" onClick={more.open}><MoreHorizontal size={18} /></button>
        </div>
      </header>
      <div className="file-preview-body">
        {!url ? (
          <div className="file-preview-placeholder muted">Loading…</div>
        ) : file.mime.startsWith("image/") ? (
          <img src={url} alt={name} />
        ) : file.mime === "application/pdf" && INLINE_PDF ? (
          <iframe src={url} title={name} />
        ) : file.mime.startsWith("audio/") ? (
          <div className="file-preview-placeholder">
            <FileAudio size={48} />
            <audio controls src={url} />
          </div>
        ) : file.mime.startsWith("video/") ? (
          <video controls src={url} />
        ) : (
          <div className="file-preview-placeholder">
            <FileIcon mime={file.mime} size={48} />
            <span>{name}</span>
            <span className="muted">{formatSize(file.size)}</span>
            <button className="btn" onClick={() => void download()}><Download size={14} /> Download</button>
          </div>
        )}
      </div>
      {more.anchor && (
        <Menu
          anchor={more.anchor}
          align="end"
          onClose={more.close}
          entries={[
            { label: "Go to file note", onSelect: goToNote },
            { label: "Download file", onSelect: () => void download() },
          ]}
        />
      )}
    </section>
  );
}
